import { Toaster as Sonner } from "sonner";
import {
  CircleAlert,
  CircleCheck,
  Info,
  LoaderCircle,
  TriangleAlert,
} from "lucide-react";

/**
 * shadcn/ui Sonner, themed to the portal.
 *
 * The card takes the portal's own tokens (background, foreground, border),
 * its 10px radius and the page font, so a toast reads as part of the screen
 * rather than a library default. The shadcn `group-[.toaster]` classes are
 * what let these win over Sonner's injected styles without `!important`:
 * they carry one more class of specificity than Sonner's own selectors.
 *
 * Type is carried by a coloured icon, not a coloured card: the same red,
 * amber and blue the claim notices use (validationNotices.jsx), and green for
 * success. Mounted once, in App.jsx; call `toast()` from "sonner" anywhere.
 *
 * No close button: toasts dismiss themselves after 6s and can be swiped away
 * (Sonner's default), and a corner X on every toast was noise.
 *
 * Text is 14px on desktop and 16px on a phone (15px for a description), where
 * 14px is too small to read at a glance, with the icon growing to match.
 */
const iconClass = "h-5 w-5 max767:h-6 max767:w-6";

const Toaster = ({ mobile = false, dir = "ltr", ...props }) => (
  <Sonner
    className="toaster group"
    dir={dir}
    position={mobile ? "top-center" : "top-right"}
    duration={6000}
    gap={8}
    mobileOffset={{ top: 16, left: 16, right: 16 }}
    // Sonner sets its own font stack on the toaster; inherit the page's
    // (Roboto, or GE SS under the .arabic root) instead.
    style={{ fontFamily: "inherit" }}
    icons={{
      success: <CircleCheck className={`${iconClass} text-[#1E8E5A]`} />,
      // An exclamation, not an X: most of the app's toasts go through
      // toast.error, including neutral ones ("you will receive an SMS"), and
      // an X read every one of them as a failure.
      error: <CircleAlert className={`${iconClass} text-[#D93F35]`} />,
      warning: <TriangleAlert className={`${iconClass} text-[#B8741A]`} />,
      info: <Info className={`${iconClass} text-[#0A7CAE]`} />,
      loading: (
        <LoaderCircle className={`${iconClass} animate-spin text-[#6C7A83]`} />
      ),
    }}
    toastOptions={{
      classNames: {
        toast:
          "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:rounded-[10px] group-[.toaster]:shadow-[0_1px_2px_rgba(26,32,36,0.06),0_8px_24px_rgba(26,32,36,0.12)] group-[.toaster]:px-4 group-[.toaster]:py-3.5 group-[.toaster]:gap-3 group-[.toaster]:items-start max767:group-[.toaster]:py-4",
        title:
          "group-[.toast]:text-sm group-[.toast]:font-medium group-[.toast]:leading-5 group-[.toast]:text-[#1A2024] max767:group-[.toast]:text-base max767:group-[.toast]:leading-[22px]",
        description:
          "group-[.toast]:text-sm group-[.toast]:leading-5 group-[.toast]:text-muted-foreground max767:group-[.toast]:text-[15px] max767:group-[.toast]:leading-[22px]",
        // Sonner boxes icons at 16px; size the box to the icon so it does
        // not spill toward the text and the 12px gap holds.
        icon: "group-[.toast]:m-0 group-[.toast]:w-5 group-[.toast]:h-5 max767:group-[.toast]:w-6 max767:group-[.toast]:h-6",
        closeButton:
          "group-[.toast]:bg-background group-[.toast]:text-[#6C7A83] group-[.toast]:border-border group-[.toast]:hover:bg-[#F6F8F9]",
        actionButton:
          "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
        cancelButton:
          "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
      },
    }}
    {...props}
  />
);

export { Toaster };
